import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getFormattedTimeFromString(timeString: string): string {
  if (!timeString) return "";
  
  try {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeString;
  }
}

// Add this function to slugify text
export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')       // Replace spaces with -
    .replace(/[^\w-]+/g, '')    // Remove all non-word chars
    .replace(/--+/g, '-')       // Replace multiple - with single -
    .replace(/^-+/, '')         // Trim - from start of text
    .replace(/-+$/, '');        // Trim - from end of text
}

// Generate an available URL slug from meeting name
export async function generateAvailableSlug(
  meetingName: string, 
  checkSlugExists: (slug: string) => Promise<boolean>,
  maxAttempts: number = 10
): Promise<string> {
  if (!meetingName?.trim()) {
    return '';
  }

  // Generate base slug from meeting name
  let baseSlug = slugify(meetingName);
  
  // Ensure minimum length
  if (baseSlug.length < 3) {
    baseSlug = baseSlug + '-meeting';
  }

  // Check if base slug is available
  const isBaseTaken = await checkSlugExists(baseSlug);
  if (!isBaseTaken) {
    return baseSlug;
  }

  // Try with numbers appended
  for (let i = 2; i <= maxAttempts; i++) {
    const candidateSlug = `${baseSlug}-${i}`;
    const isTaken = await checkSlugExists(candidateSlug);
    if (!isTaken) {
      return candidateSlug;
    }
  }

  // If all attempts fail, return base slug with timestamp
  const timestamp = Date.now().toString().slice(-6);
  return `${baseSlug}-${timestamp}`;
}

export function luminance([r, g, b]: number[]): number {
  const a = [r, g, b].map(v => {
    v /= 255;
    return v <= 0.03928
      ? v / 12.92
      : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

export function contrastRatio(rgb1: number[], rgb2: number[]): number {
  const lum1 = luminance(rgb1);
  const lum2 = luminance(rgb2);
  return (Math.max(lum1, lum2) + 0.05) /
         (Math.min(lum1, lum2) + 0.05);
}

export function getContrastTextColor(backgroundColor: string): string {
  if (!backgroundColor) return 'black';
  
  // Check if it's a hex color (existing logic)
  if (backgroundColor.startsWith('#')) {
    try {
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      const white = [255, 255, 255];
      const black = [0, 0, 0];
      const background = [r, g, b];

      const useWhite = contrastRatio(white, background) > contrastRatio(black, background);
      return useWhite ? 'white' : 'black';
    } catch (error) {
      console.warn('Invalid hex color:', backgroundColor);
      return 'black';
    }
  }
  
  // Check if it's an RGB/RGBA color
  if (backgroundColor.startsWith('rgb')) {
    try {
      const rgbMatch = backgroundColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1]);
        const g = parseInt(rgbMatch[2]);
        const b = parseInt(rgbMatch[3]);

        const white = [255, 255, 255];
        const black = [0, 0, 0];
        const background = [r, g, b];

        const useWhite = contrastRatio(white, background) > contrastRatio(black, background);
        return useWhite ? 'white' : 'black';
      }
    } catch (error) {
      console.warn('Invalid RGB color:', backgroundColor);
    }
  }
  
  // For gradients, images, and videos, use intelligent fallbacks
  const backgroundType = getBackgroundType(backgroundColor);
  
  switch (backgroundType) {
    case 'gradient':
      return getContrastTextColorForGradient(backgroundColor);
    case 'image':
    case 'video':
      // For images and videos, assume dark content and use white text
      // This is a common pattern for better readability
      return 'white';
    default:
      // Fallback to white text for unknown types
      return 'white';
  }
}

export function getBackgroundType(background: string): 'gradient' | 'image' | 'video' | 'color' | 'unknown' {
  if (!background) return 'unknown';
  
  // Check for gradients
  if (background.includes('gradient')) return 'gradient';
  
  // Check for data URLs first (before other URL checks)
  if (background.startsWith('data:')) {
    if (background.startsWith('data:video/')) return 'video';
    if (background.startsWith('data:image/')) return 'image';
    // Default data URLs to image for backward compatibility
    return 'image';
  }
  
  // Check for video files
  if (background.includes('.mp4') || background.includes('.webm') || background.includes('.mov')) return 'video';
  
  // Check for image files
  if (background.includes('.jpg') || background.includes('.jpeg') || background.includes('.png') || 
      background.includes('.webp') || background.includes('.svg') || background.includes('.gif')) return 'image';
  
  // Check for HTTP URLs (likely images)
  if (background.startsWith('http://') || background.startsWith('https://')) return 'image';
  
  // Check for hex colors
  if (/^#[0-9A-F]{6}$/i.test(background)) return 'color';
  
  // Check for rgb/rgba colors
  if (background.startsWith('rgb(') || background.startsWith('rgba(')) return 'color';
  
  return 'unknown';
}

function getContrastTextColorForGradient(gradient: string): string {
  // Extract colors from gradient and analyze them
  const colors = extractColorsFromGradient(gradient);
  
  if (colors.length === 0) {
    return 'white'; // Fallback
  }
  
  // Calculate average luminance of all colors in the gradient
  let totalLuminance = 0;
  let validColors = 0;
  
  for (const color of colors) {
    try {
      const rgb = hexToRgb(color);
      if (rgb) {
        totalLuminance += luminance(rgb);
        validColors++;
      }
    } catch (error) {
      // Skip invalid colors
    }
  }
  
  if (validColors === 0) {
    return 'white'; // Fallback
  }
  
  const averageLuminance = totalLuminance / validColors;
  
  // If average luminance is high (light colors), use black text
  // If average luminance is low (dark colors), use white text
  return averageLuminance > 0.5 ? 'black' : 'white';
}

function extractColorsFromGradient(gradient: string): string[] {
  const colors: string[] = [];
  
  // Extract hex colors
  const hexMatches = gradient.match(/#[0-9A-F]{6}/gi) || [];
  colors.push(...hexMatches);
  
  // Extract rgb colors and convert to hex
  const rgbMatches = gradient.match(/rgba?\([^)]+\)/gi) || [];
  for (const rgb of rgbMatches) {
    const hex = rgbToHex(rgb);
    if (hex) colors.push(hex);
  }
  
  return colors;
}

function hexToRgb(hex: string): number[] | null {
  try {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : null;
  } catch (error) {
    return null;
  }
}

function rgbToHex(rgb: string): string | null {
  try {
    const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
    }
    return null;
  } catch (error) {
    return null;
  }
}

// Extract domain from URL
export const extractDomain = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace("www.", "");
  } catch {
    return null;
  }
};

// Fetch logo from Clearbit
export const fetchClearbitLogo = async (websiteUrl: string): Promise<string | null> => {
  if (!websiteUrl) return null;
  
  const domain = extractDomain(websiteUrl);
  if (!domain) return null;
  
  try {
    const clearbitUrl = `https://logo.clearbit.com/${domain}`;
    
    // Test if the logo exists
    const response = await fetch(clearbitUrl, { method: 'HEAD' });
    if (response.ok) {
      return clearbitUrl;
    }
  } catch (error) {
    console.log('Clearbit logo not found for domain:', domain);
  }
  
  return null;
};
